import { create } from 'zustand';
import { useSelectedOrganizationId } from 'stores/useAuthStore';
import { useTimesheetErps } from 'containers/Timesheets/hooks/useTimesheetErps';
import {
  erpSupportsTimesheets,
  type AllPossibleIntegrationSettings,
  type Organization,
  type OrganizationsState,
} from 'containers/Organizations/organizations.types';


type OrgList = OrganizationsState['organizations'];
type OrgSettingsMap = Record<
  Organization['organizationId'],
  Partial<AllPossibleIntegrationSettings>
> | null;

const INITIAL: {
  organizations: OrgList;
  organizationSettingsMapByOrgId: OrgSettingsMap;
} = {
  organizations: { values: [], lastUpdated: undefined },
  organizationSettingsMapByOrgId: null,
};

interface OrganizationsStoreState {
  organizations: OrgList;
  organizationSettingsMapByOrgId: OrgSettingsMap;
  setOrganizations: (organizations: OrgList) => void;
  setOrganizationSettingsMap: (map: NonNullable<OrgSettingsMap>) => void;
  reset: () => void;
}

export const useOrganizationsStore = create<OrganizationsStoreState>((set) => ({
  ...INITIAL,
  setOrganizations: (organizations) => set({ organizations }),
  setOrganizationSettingsMap: (organizationSettingsMapByOrgId) =>
    set({ organizationSettingsMapByOrgId }),
  reset: () => set({ ...INITIAL }),
}));

export const setOrganizations = (organizations: OrgList) =>
  useOrganizationsStore.getState().setOrganizations(organizations);
export const setOrganizationSettingsMap = (map: NonNullable<OrgSettingsMap>) =>
  useOrganizationsStore.getState().setOrganizationSettingsMap(map);
export const resetOrganizations = () =>
  useOrganizationsStore.getState().reset();

export const getOrganizations = () =>
  useOrganizationsStore.getState().organizations;
export const getOrganizationSettingsMap = () =>
  useOrganizationsStore.getState().organizationSettingsMapByOrgId;

export const useOrganizationsList = () =>
  useOrganizationsStore((s) => s.organizations);
export const useOrganizationSettingsMap = () =>
  useOrganizationsStore((s) => s.organizationSettingsMapByOrgId);

export const useSelectedOrganizationSettings = () => {
  const map = useOrganizationsStore((s) => s.organizationSettingsMapByOrgId);
  const selectedOrganizationId = useSelectedOrganizationId();
  return map?.[selectedOrganizationId] ?? null;
};

export const useSelectedOrganizationTimeZone = () => {
  const settings = useSelectedOrganizationSettings();
  return typeof settings?.timeZone === 'string' ? settings.timeZone : '';
};

export const useIsHalEnabled = () => {
  const settings = useSelectedOrganizationSettings();
  return settings?.['feature.harmoniAi.enabled'] === 'true';
};

export const useIsTimesheetsEnabled = () => {
  const settings = useSelectedOrganizationSettings();
  const timesheetErps = useTimesheetErps();
  const erpSelected = settings?.['ERP.selected'];
  return (
    settings?.['feature.timesheets.enabled'] === 'true' &&
    !!timesheetErps &&
    erpSupportsTimesheets(
      timesheetErps,
      erpSelected,
      erpSelected ? settings?.[`ERP.${erpSelected}.version`] : undefined
    )
  );
};

export const useIsActivityScrapPredictorEnabled = () => {
  const settings = useSelectedOrganizationSettings();
  return (
    settings?.['feature.activity.scrapPredictor.enabled'] === 'true'
  );
};
